<template>
  <main>
    <section-view class="header" :title="$t('titles.tissue')">
      <div class="name fx">
        <span>
          <b>{{title}}</b>
          <i></i>
        </span>
      </div>
    </section-view>

    <!-- 项目说明 -->
    <text-card
        v-if="explain"
        :title="$t('tissue.projectDescription')"
        :content="explain"
    ></text-card>

    <waterfall-card
        v-if="dynamics.length > 0"
        :title="$t('tissue.dynamicTrackingMode')"
        :list="dynamics"
        labelKey="time"
        :valueKey="stackValueKey"
        stack="diff"
        line="av"
        format
        tips
        @tip="interpret = true"
    ></waterfall-card>

    <!-- 平均值状态图 -->
    <waterfall-card
        v-if="averageStatus.length > 0"
        :title="$t('tissue.averageStatusChart')"
        :list="averageStatus"
        labelKey="time"
        colorKey="color"
        :valueKey="avValueKey"
        format
    ></waterfall-card>

    <!-- 影响身体关联项 -->
    <tag-card :title="$t('tissue.bodyAssociatedItems')" :list="relations" @selected="row => $router.push({path: '/details', query: {code: row.system_code, title: row.item_dict_name, color: row.color, ...query}})"></tag-card>

    <!-- 饮食建议 -->
    <text-card
        :title="$t('tissue.dietaryRecommendations')"
        :content="diet"
    ></text-card>

    <!-- 运动建议 -->
    <text-card
        :title="$t('tissue.exerciseRecommendations')"
        :content="motion"
    ></text-card>

    <!-- 起居建议 -->
    <text-card
        :title="$t('tissue.lifestyleRecommendations')"
        :content="daily"
    ></text-card>

    <!-- 解读说明弹窗 -->
    <dialog-view v-model="interpret" class="interpret" :title="$t('tissue.interpretationGuide')">
      <span v-for="(item, index) in levels" :key="index"><i :style="{background: item.color}"></i>{{item.str}}</span>
    </dialog-view>
  </main>
</template>

<script>
  import {getReportLoopItemOrgans} from "../net/api";
  import SectionView from "../components/SectionView";
  import TextCard from "../components/TextCard";
  import TagCard from "../components/TagCard";
  import WaterfallCard from "../components/WaterfallCard";
  import DialogView from "../components/DialogView";

  export default {
    name: "Nutrition",
    components: {DialogView, WaterfallCard, TagCard, TextCard, SectionView},

    data() {
      return {
        packageNo: '',
        checkcode: '',
        rand: '',

        code: '',
        title: '',

        // 影响身体的关联项
        relations: [],
        // 项目说明
        explain: '',
        // 饮食建议
        diet: '',
        // 运动建议
        motion: '',
        // 起居建议
        daily: '',

        stackValueKey: {},
        avValueKey: {},
        // 平均值状态图
        averageStatus: [],

        // 动态跟踪模式
        dynamics: [],

        // 报告等级列表
        levels: [],

        // 解读说明弹窗
        interpret: false
      }
    },

    computed: {
      // 基础参数数据
      query() {
        return {
          packageNo: this.packageNo, rand: this.rand,  checkcode: this.checkcode
        }
      }
    },

    watch: {
      // 监听语言变化，重新翻译状态描述
      '$i18n.locale'() {
        // 重新初始化图表键值对象
        this.stackValueKey = {
          one: this.$t('charts.elements.initialValue'),
          two: this.$t('charts.elements.finalValue'),
        };
        this.avValueKey = {
          av: this.$t('charts.elements.averageValue'),
        };
        
        // 重新翻译状态等级描述
        if (this.levels && this.levels.length > 0) {
          this.levels = this.levels.map(level => ({
            ...level,
            str: this.translateOrganStatus(level.originalStr || level.str)
          }));
        }
      }
    },

    created() {
      const query = this.$router.currentRoute.query;
      this.packageNo = query.packageNo;
      this.checkcode = query.checkcode;
      this.rand = query.rand;

      this.title = query.title;
      this.code = query.code;

      // 初始化图表键值对象
      this.stackValueKey = {
        one: this.$t('charts.elements.initialValue'),
        two: this.$t('charts.elements.finalValue'),
      };
      this.avValueKey = {
        av: this.$t('charts.elements.averageValue'),
      };

      this.syncPageData();
    },

    methods: {
      // 翻译器官状态描述
      translateOrganStatus(statusText) {
        if (!statusText) return statusText;
        
        // 状态翻译映射表
        const statusMap = {
          '正常': this.$t('health.status.normal'),
          '异常': this.$t('health.status.abnormal'),
          '优异': this.$t('health.status.excellent'),
          '良好': this.$t('health.status.good'),
          '一般': this.$t('health.status.fair'),
          '较差': this.$t('health.status.poor'),
          '严重': this.$t('health.status.critical'),
          '健康': this.$t('health.status.healthy'),
          '亚健康': this.$t('health.status.subHealth'),
          '预警': this.$t('health.status.warning'),
          '风险': this.$t('health.status.risk'),
          '注意': this.$t('health.status.attention'),
          '保持': this.$t('health.status.maintain'),
          '加强': this.$t('health.status.strengthen'),
          '改善': this.$t('health.status.improve'),
          '改善中': this.$t('health.status.improving'),
          '稳定': this.$t('health.status.stable'),
          '下降': this.$t('health.status.declining')
        };
        
        return statusMap[statusText] || statusText;
      },

      // 同步页面数据
      syncPageData() {
        // 获取报告数据
        getReportLoopItemOrgans({
          packageNo: this.packageNo,
          rand: this.rand,
          checkcode: this.checkcode,

          system_code: this.code
        }).then((data) => {
          // 处理状态等级数据，翻译状态描述
          this.levels = data.colorData ? data.colorData.map(level => ({
            ...level,
            originalStr: level.str, // 保存原始文本
            str: this.translateOrganStatus(level.str)
          })) : [];
          
          this.relations = data.show_shentiguanlian;
          this.explain = data.item_explain;
          this.averageStatus = data.show_pingjunzhi;
          this.diet = data.show_yinshi;
          this.motion = data.show_yundong;
          this.daily = data.show_qiju;
          this.dynamics = data.show_dongtai;
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .header {
    .name {
      position: relative;
      padding-top: 60px;

      span {
        position: relative;
        padding: 0 10px;
      }

      b {
        position: relative;
        height: 36px;
        font-size: 50px;
        color: #333333;
        line-height: 36px;
        font-weight: 500;
        z-index: 1;
      }

      i {
        position: absolute;
        width: 100%;
        left: 0;
        bottom: -14px;
        content: "";
        display: block;
        height: 18px;
        background: #6CA0EC;
        border-radius: 9px;
      }
    }
  }
</style>